import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SnakShell } from './components/SnakShell';
import { OpeningScreen } from './components/screens/OpeningScreen';
import { ResponseScreen } from './components/screens/ResponseScreen';
import { TransitionScreen } from './components/screens/TransitionScreen';
import { FamilyScreen } from './components/screens/FamilyScreen';
import { ProductScreen } from './components/screens/ProductScreen';
import { PointScreen } from './components/screens/PointScreen';
import { CloseScreen } from './components/screens/CloseScreen';
import { products } from './data/products';
import type { SiblingRole, Tone } from './types/snak';
const TOTAL = 9;
interface AppProps {
  /** Show the step progress rail in the header. */
  showProgress?: boolean;
  /** Let the "Families are funny" interlude play itself, or require a tap per line. */
  autoplayInterlude?: boolean;
}
export function App({
  showProgress = true,
  autoplayInterlude = true
}: AppProps) {
  const [step, setStep] = useState(0);
  const [role, setRole] = useState<SiblingRole | null>(null);
  const [interludeDark, setInterludeDark] = useState(false);
  const next = useCallback(() => setStep(s => Math.min(s + 1, TOTAL - 1)), []);
  const chooseRole = (picked: SiblingRole) => {
    if (role) return;
    setRole(picked);
    window.setTimeout(() => setStep(1), 620);
  };
  const restart = () => {
    setRole(null);
    setInterludeDark(false);
    setStep(0);
  };
  const tone: Tone = step <= 1 ? 'light' : step === 2 ? interludeDark ? 'dark' : 'light' : 'dark';
  const renderStep = () => {
    switch (step) {
      case 0:
        return <OpeningScreen selected={role} onSelect={chooseRole} data-id="element-0" data-mp="38o4fjf2g6l5t" />;
      case 1:
        return <ResponseScreen role={role ?? 'firstborn'} onNext={next} data-id="element-1" data-mp="3diruonbka37q" />;
      case 2:
        return <TransitionScreen autoplay={autoplayInterlude} onDarken={setInterludeDark} onNext={next} data-id="element-2" data-mp="nxn6nvnsp9oy" />;
      case 3:
        return <FamilyScreen onNext={next} data-id="element-3" data-mp="356wxj89s6fx" />;
      case 4:
      case 5:
      case 6:
        return <ProductScreen product={products[step - 4]} position={step - 3} total={products.length} onNext={next} data-id="element-4" data-mp="3ubzvsgflbtha" />;
      case 7:
        return <PointScreen onNext={next} data-id="element-5" data-mp="864afiic4cb3" />;
      default:
        return <CloseScreen onRestart={restart} data-id="element-6" data-mp="3gkv0trn63pda" />;
    }
  };
  return <SnakShell tone={tone} step={step} total={TOTAL} showProgress={showProgress} data-id="element-7" data-mp="3gja6o34rm8fy">
      <AnimatePresence mode="wait" data-id="element-8" data-mp="3bnuvxk220snr">
        <motion.div key={step} initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -14
      }} transition={{
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex flex-1 flex-col" data-id="element-9" data-mp="3qb7k7gj8xchu">
          {renderStep()}
        </motion.div>
      </AnimatePresence>
    </SnakShell>;
}